import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {BoxedInputStatic} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputStatic';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {maybeGetPreviousPeriodFormatter} from '../../../../helpers/analyticsFormatHelpers';
import {ChartSerie} from '../../../charts/commons/types';
import {ChartLegendBadge} from '../../../charts/components/legend/chartLegendBadge';
import {StyledFlex} from '../../../reportViewer/reportCommons';

/*
 * Props.
 */

interface PreviousToggleProps<T> {
  shouldDisplayPrevious: boolean;
  toggleShouldDisplayPrevious: () => void;
  chartSeries: Array<ChartSerie<T>>;
  previousPeriod: {
    start?: DateTime;
    end?: DateTime;
  };
}

/*
 * Styling.
 */

const StyledCloseButton = styled(IconButton)`
  padding: 7px;
`;

const StyledPreviousPeriodWrapperDiv = styled.div`
  border-radius: 8px;
  background-color: ${(p) => p.theme.greys.shade20};
  display: flex;
`;

/*
 * Component.
 */

export const PreviousToggle = <T,>(props: PreviousToggleProps<T>): ReturnType<FC> => {
  const {shouldDisplayPrevious, chartSeries, previousPeriod, toggleShouldDisplayPrevious} = props;
  if (shouldDisplayPrevious && chartSeries.length > 1) {
    return (
      <StyledFlex>
        <ChartLegendBadge color={chartSeries[1].color} />

        <StyledPreviousPeriodWrapperDiv>
          <BoxedInputStatic
            size={VisualSizesEnum.MEDIUM}
            value={maybeGetPreviousPeriodFormatter(previousPeriod.start, previousPeriod.end)}
            inputId="previous-period-static-value"
          />
          <StyledCloseButton
            size={VisualSizesEnum.MEDIUM}
            iconName="closeSmall"
            label={<Trans id="analytics-hide-previous-period">Hide previous period</Trans>}
            onClick={toggleShouldDisplayPrevious}
          />
        </StyledPreviousPeriodWrapperDiv>
      </StyledFlex>
    );
  }

  return (
    <Button onClick={toggleShouldDisplayPrevious} variant={ButtonVariantsEnum.LINK}>
      <Trans id="analytics-compare-previous-period">Compare to previous period</Trans>
    </Button>
  );
};
